import React from "react";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Hero from "components/hero/TwoColumnWithInput.js";
import Features from "components/features/VerticalWithAlternateImageAndText.js";
import MainFeature from "components/features/TwoColWithSteps.js";
import MainFeature2 from "components/features/TwoColWithTwoFeaturesAndButtons.js";
import Pricing from "components/pricing/TwoPlansWithDurationSwitcher.js";
import Blog from "components/blogs/ThreeColSimpleWithImageAndDashedBorder.js";
import Testimonial from "components/testimonials/TwoColumnWithImageAndRating.js";
import GetStarted from "components/cta/GetStarted.js";
import Footer from "components/footers/FiveColumnWithBackground.js";
import FAQ from "components/faqs/SimpleWithSideImage.js";
import { ReactComponent as PhoneIcon } from "feather-icons/dist/icons/smartphone.svg";
import { ReactComponent as VideoIcon } from "feather-icons/dist/icons/video.svg";

const Subheading = tw.span`uppercase tracking-widest font-bold text-primary-500`;
const HighlightedText = tw.span`text-primary-500`;
const HighlightedTextInverse = tw.span`bg-primary-500 text-white px-4 transform -skew-x-12 inline-block`;

// Componentes para Anamnese IA - Design moderno com cards
const IASection = styled.section`
  ${tw`py-24 bg-white`}
`;

const IAContainer = tw.div`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8`;

const IAHeader = tw.div`text-center max-w-3xl mx-auto mb-16`;

const IATitle = tw.h2`text-4xl md:text-5xl font-bold text-gray-900 mb-6`;

const IADescription = tw.p`text-xl text-gray-600 leading-relaxed`;

const IAGrid = tw.div`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center`;

const IAImageContainer = tw.div`relative`;

const IAImage = tw.img`w-full rounded-3xl shadow-2xl`;

const IABadge = styled.div`
  ${tw`absolute bg-primary-500 text-white px-6 py-3 rounded-full font-bold text-sm shadow-lg`}
  top: -1rem;
  right: -1rem;
`;

const IAFeaturesContainer = styled.div`
  > * + * {
    margin-top: 2rem;
  }
`;

const IAFeatureCard = styled.div`
  ${tw`p-8 rounded-2xl border`}
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%);
  border-color: rgba(99, 102, 241, 0.1);
`;

const IAFeatureHeader = tw.div`flex items-center mb-4`;

const IAFeatureIcon = styled.div`
  ${tw`w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center mr-4`}
`;

const IAFeatureTitle = tw.h3`text-xl font-bold text-gray-900`;

const IAFeatureDescription = tw.p`text-gray-600 leading-relaxed`;

const IASecuritySection = styled.div`
  ${tw`mt-12 p-8 rounded-2xl border-2`}
  background-color: rgba(16, 185, 129, 0.05);
  border-color: rgba(16, 185, 129, 0.1);
`;

const IASecurityTitle = styled.h4`
  ${tw`text-lg font-bold mb-3 flex items-center`}
  color: #047857;
`;

const IASecurityText = styled.p`
  ${tw``}
  color: #059669;
`;

export default () => {
  return (
    <AnimationRevealPage>
      <Hero 
        heading="Transforme sua prática clínica com tecnologia"
        description="Plataforma completa para profissionais de fonoaudiologia e odontologia. Conecte-se com seus pacientes, otimize tratamentos e aumente a eficácia com recursos interativos."
        submitButtonText="Começar Gratuitamente"
        inputPlaceholder="Digite seu email profissional"
        imageSrc="../doctorHappy.jpg"
        customersLogoStripText="Mais de 500 pacientes já confiam na Trataz"
      />
      
      <Features
        subheading="Como Funciona"
        heading={
          <>
            Processo <HighlightedText>Simples e Eficaz</HighlightedText>
          </>
        }
        description="Três passos para revolucionar seus atendimentos e conectar-se verdadeiramente com seus pacientes."
        cards={[
          {
            imageSrc: "../fluxo-de-trabalho.png",
            subtitle: "Passo 1",
            title: "Cadastre-se na Plataforma",
            description: "Crie seu perfil profissional e configure sua conta em poucos minutos. Personalize sua página e comece a usar as ferramentas exclusivas.",
            url: "https://www.trataz.com.br/register"
          },
          {
            imageSrc: "../pessoas.png",
            subtitle: "Passo 2", 
            title: "Conecte com Pacientes",
            description: "Adicione seus pacientes à plataforma e compartilhe materiais didáticos interativos. Envie lembretes automáticos por WhatsApp e email.",
            url: "https://www.trataz.com.br/register"
          },
          {
            imageSrc: "../otimizacao.png",
            subtitle: "Passo 3",
            title: "Monitore o Progresso",
            description: "Acompanhe a evolução dos tratamentos em tempo real. Receba feedback dos pacientes e otimize suas consultas com dados precisos.",
            url: "https://www.trataz.com.br/register"
          }
        ]}
      />

      <MainFeature
        subheading={<Subheading>Processo Completo</Subheading>}
        heading={
          <>
            Como a Trataz <HighlightedText>Funciona</HighlightedText>
          </>
        }
        imageSrc="../notebook.png"
        imageRounded={true}
        imageBorder={true}
        imageShadow={true}
        steps={[
          {
            heading: "Cadastro Profissional",
            description: "Registre-se como profissional de saúde e configure seu perfil. Validamos sua formação para garantir credibilidade."
          },
          {
            heading: "Gestão de Pacientes",
            description: "Adicione pacientes, crie planos de tratamento personalizados e compartilhe materiais interativos específicos."
          },
          {
            heading: "Acompanhamento Ativo",
            description: "Monitore exercícios, receba feedback e mantenha comunicação constante através de lembretes automáticos."
          },
          {
            heading: "Resultados Comprovados",
            description: "Analise métricas de progresso e otimize tratamentos baseado em dados reais de engajamento."
          }
        ]}
      />

      <MainFeature2
        subheading={<Subheading>Recursos Exclusivos</Subheading>}
        heading={
          <>
            Ferramentas <HighlightedText>Profissionais</HighlightedText>
          </>
        }
        description="Tecnologia avançada desenvolvida especificamente para profissionais de fonoaudiologia e odontologia."
        features={[
          {
            Icon: () => <div tw="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white"><PhoneIcon tw="w-4 h-4" /></div>,
            title: "App Mobile Completo",
            description: "Seus pacientes têm acesso a todos os recursos através de um app intuitivo e fácil de usar."
          },
          {
            Icon: () => <div tw="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white"><VideoIcon tw="w-4 h-4" /></div>,
            title: "Vídeos Interativos",
            description: "Grave exercícios personalizados e envie diretamente para seus pacientes com instruções claras."
          }
        ]}
        primaryButtonText="Experimentar Grátis"
        primaryButtonUrl="https://www.trataz.com.br/register"
        secondaryButtonText="Agendar Demo"
        secondaryButtonUrl="https://wa.me/5547996912977"
      />

      <Pricing
        subheading={<Subheading>Planos</Subheading>}
        heading={
          <>
            Escolha seu <HighlightedText>Plano</HighlightedText>
          </>
        }
        description="Comece gratuitamente e escale conforme sua prática cresce. Sem compromisso, cancele quando quiser."
        primaryButtonText="Começar Agora"
        planDurations={[
          { 
            text: "Mês", 
            switcherText: "Mensal" 
          },
          { 
            text: "Ano", 
            switcherText: "Anual" 
          }
        ]}
        plans={[
          {
            name: "Starter",
            durationPrices: ["R$ 97", "R$ 970"],
            mainFeature: "Até 30 pacientes",
            features: [
              "Materiais didáticos básicos",
              "Lembretes por WhatsApp", 
              "Suporte por email",
              "Relatórios básicos"
            ]
          },
          {
            name: "Profissional",
            durationPrices: ["R$ 197", "R$ 1.970"],
            mainFeature: "Pacientes ilimitados",
            features: [
              "Todos os recursos do Starter",
              "Vídeos personalizados",
              "Suporte prioritário",
              "Relatórios avançados",
              "Integração com agenda"
            ],
            featured: true
          }
        ]}
      />

      <IASection>
        <IAContainer>
          <IAHeader>
            <IATitle>
              Anamnese Inteligente com <HighlightedText>IA</HighlightedText>
            </IATitle>
            <IADescription>
              Revolucione suas consultas com nossa tecnologia de ponta. 
              Nossa IA escuta, transcreve e organiza automaticamente todas as informações importantes, 
              criando relatórios precisos e personalizados para cada paciente.
            </IADescription>
          </IAHeader>

          <IAGrid>
            <IAImageContainer>
              <IAImage 
                src="https://images.unsplash.com/photo-1504813184591-01572f98c85f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="IA analisando consulta médica"
                onError={(e) => {
                  e.target.src = "../doctorHappy.jpg";
                }}
              />
              <IABadge>🤖 IA Avançada</IABadge>
            </IAImageContainer>

            <IAFeaturesContainer>
              <IAFeatureCard>
                <IAFeatureHeader>
                  <IAFeatureIcon>
                    <svg width="16" height="16" fill="white" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                    </svg>
                  </IAFeatureIcon>
                  <IAFeatureTitle>Escuta Inteligente</IAFeatureTitle>
                </IAFeatureHeader>
                <IAFeatureDescription>
                  Nossa IA captura e processa automaticamente toda a conversa durante a consulta, 
                  identificando informações clínicas relevantes e sintomas importantes.
                </IAFeatureDescription>
              </IAFeatureCard>

              <IAFeatureCard>
                <IAFeatureHeader>
                  <IAFeatureIcon>
                    <svg width="16" height="16" fill="white" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 104 0 2 2 0 00-4 0zm6 0a2 2 0 104 0 2 2 0 00-4 0z" clipRule="evenodd" />
                    </svg>
                  </IAFeatureIcon>
                  <IAFeatureTitle>Relatórios Automáticos</IAFeatureTitle>
                </IAFeatureHeader>
                <IAFeatureDescription>
                  Gera automaticamente relatórios estruturados e personalizados para fonoaudiologia e odontologia, 
                  destacando pontos-chave e recomendações específicas.
                </IAFeatureDescription>
              </IAFeatureCard>

              <IAFeatureCard>
                <IAFeatureHeader>
                  <IAFeatureIcon>
                    <svg width="16" height="16" fill="white" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                    </svg>
                  </IAFeatureIcon>
                  <IAFeatureTitle>Economia de Tempo</IAFeatureTitle>
                </IAFeatureHeader>
                <IAFeatureDescription>
                  Reduza em até 70% o tempo gasto com documentação. 
                  Foque no que realmente importa: o cuidado com seus pacientes.
                </IAFeatureDescription>
              </IAFeatureCard>
            </IAFeaturesContainer>
          </IAGrid>

          <IASecuritySection>
            <IASecurityTitle>
              <svg width="20" height="20" className="mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Segurança e Privacidade Garantidas
            </IASecurityTitle>
            <IASecurityText>
              <strong>100% em conformidade com a LGPD.</strong> Todos os dados são criptografados, 
              armazenados em servidores brasileiros e jamais compartilhados com terceiros. 
              Você mantém total controle sobre as informações dos seus pacientes.
            </IASecurityText>
          </IASecuritySection>
        </IAContainer>
      </IASection>

      <Testimonial
        subheading=""
        heading={
          <>
            Profissionais <HighlightedText>Recomendam</HighlightedText>
          </>
        }
        description="Veja como a Trataz está transformando a prática clínica de profissionais em todo o Brasil."
        testimonials={[
          {
            stars: 5,
            profileImageSrc: "../edilaine_profissional.jpg",
            heading: "Revolucionou minha prática",
            quote: "A Trataz mudou completamente como me relaciono com meus pacientes. O engajamento aumentou 300% e os resultados são visíveis muito mais rápido.",
            customerName: "Dra. Edilaine Tannouri",
            customerTitle: "Fonoaudióloga - 25 anos de experiência"
          },
          {
            stars: 5, 
            profileImageSrc: "../carlos_antonio.jpg",
            heading: "Pacientes mais dedicados",
            quote: "Antes os pacientes esqueciam dos exercícios. Agora, com os lembretes automáticos e vídeos personalizados, a adesão ao tratamento é quase total.",
            customerName: "Dr. Antônio Carlos",
            customerTitle: "Cirurgião-Dentista"
          }
        ]}
      />

      <FAQ
        subheading={<Subheading>Dúvidas Frequentes</Subheading>}
        heading={
          <>
            Perguntas <HighlightedText>Comuns</HighlightedText>
          </>
        }
        description="Tire suas dúvidas sobre como a Trataz pode transformar sua prática profissional."
        imageSrc="../tratamentImg.jpg"
        faqs={[
          {
            question: "Como funciona o período de teste?",
            answer: "Oferecemos 7 dias gratuitos para você testar todos os recursos da plataforma. Sem compromisso, sem cartão de crédito."
          },
          {
            question: "Posso usar com qualquer especialidade?",
            answer: "A Trataz foi desenvolvida especificamente para fonoaudiologia e odontologia, com recursos otimizados para essas áreas."
          },
          {
            question: "Os pacientes precisam pagar alguma coisa?",
            answer: "Não! Seus pacientes têm acesso gratuito ao app e a todos os materiais que você compartilhar."
          },
          {
            question: "Como funciona o suporte técnico?",
            answer: "Oferecemos suporte por email, WhatsApp e chat ao vivo. Profissionais do plano Premium têm atendimento prioritário."
          },
          {
            question: "É seguro para dados dos pacientes?",
            answer: "Sim! Seguimos rigorosamente a LGPD e todas as informações são criptografadas e armazenadas com segurança."
          }
        ]}
      />

      <Blog
        subheading="Blog"
        heading={
          <>
            Conteúdo <HighlightedText>Exclusivo</HighlightedText>
          </>
        }
        description="Artigos, dicas e insights para profissionais de fonoaudiologia e odontologia que querem se destacar no mercado e melhorar seus resultados."
      />

      <GetStarted
        text={
          <>
            Mais de 500 profissionais já estão usando a{" "}
            <HighlightedTextInverse>Trataz</HighlightedTextInverse> para transformar seus atendimentos.
          </>
        }
        primaryButtonText="Começar Gratuitamente"
        primaryButtonUrl="https://www.trataz.com.br/login"
        secondaryButtonText="Agendar Demo"
        secondaryButtonUrl="https://wa.me/5547996912977"
      />

      <Footer />
    </AnimationRevealPage>
  );
}; 
